
import * as React from 'react';
import { EventGroup, Async } from 'office-ui-fabric-react/lib/Utilities';
import { IEntry, buildEntriesFromFileList } from '../../utilities/fileSystem/FileEntries';
import * as FileSystemDialogStyles from './FileSystemDialog.scss';

/**
 * Props for a `FileSystemDialog` component.
 * @public
 */
export interface IFileSystemDialogProps {
  onContinue: (entries: IEntry[]) => void;
  onCancel: () => void;
  type?: 'file' | 'directory';
}

/**
 * @public
 */
export interface IFileSystemDialogState {
  /**
   * A reference to the input element.
   * Needed to determine when the element exists,
   * and to attach properties to it not supported by React.
   */
  inputElement: HTMLInputElement | null;
  /**
   * The files selected from the real dialog.
   */
  files: File[] | undefined;
  /**
   * Signals that nothing has happened for a bit after
   * the body has received focus.
   * Used as a timeout if the input element never fired a change event.
   */
  hasNothingHappened: boolean;
}

// Wait one hour for the user to make a choice.
const NOTHING_HAPPENED_TIMEOUT_MS = 60 * 60 * 1000;

const FILE_DIALOG_INITIAL_STATE: IFileSystemDialogState = {
  inputElement: null,
  files: undefined,
  hasNothingHappened: false
};

/**
 * Component which wraps invocation of a file selector from the file system.
 * @public
 */
export class FileSystemDialog extends React.PureComponent<IFileSystemDialogProps, IFileSystemDialogState> {
  private _events: EventGroup;
  private _async: Async;

  constructor(props: IFileSystemDialogProps) {
    super(props);

    this.state = FILE_DIALOG_INITIAL_STATE;

    this._events = new EventGroup(this);
    this._async = new Async(this);
  }

  public render(): JSX.Element {
    return (
      <input
        className={ FileSystemDialogStyles.input }
        data-automationid={ this.props.type === 'directory' ? 'commandFolderInput' : 'commandFileInput' }
        type='file'
        ref={ this._onInputRef }
        multiple={ true }
        onChange={ this._onInputChange }
      />
    );
  }

  public componentDidMount(): void {
    this._onStateChanged(FILE_DIALOG_INITIAL_STATE);
  }

  public componentDidUpdate(previousProps: IFileSystemDialogProps, previousState: IFileSystemDialogState): void {
    this._onStateChanged(previousState);
  }

  public componentWillUnmount(): void {
    this._events.dispose();
    this._async.dispose();
  }

  private _onStateChanged(previousState: IFileSystemDialogState): void {
    const {
      files,
      inputElement,
      hasNothingHappened
    } = this.state;

    const {
      onContinue,
      onCancel
    } = this.props;

    if (!previousState.files && files) {
      // The user has selected file from the dialog.
      const entries = buildEntriesFromFileList(files);

      onContinue(entries);
    } else if (!previousState.hasNothingHappened && hasNothingHappened) {
      // The timeout waiting for the dialog to close has elapsed.
      onCancel();
    } else if (!previousState.inputElement && inputElement) {
      if (this.props.type === 'directory') {
        (inputElement as {
          webkitdirectory?: boolean;
        }).webkitdirectory = true;
      }

      // Set a timeout because the browser will send no notification if the user cancels.
      // In addition, some browsers are async with respect to firing the event,
      // so there is no guarantee that a callback will ever be invoked.

      this._async.setTimeout(this._onNothingHappened, NOTHING_HAPPENED_TIMEOUT_MS);

      // The browser will only open a dialog if this click happens synchronously
      // within the handler for a UX event.
      inputElement.click();
    }
  }

  private _onInputRef = (inputElement: HTMLInputElement | null): void => {
    this.setState({
      inputElement: inputElement
    });
  }

  private _onInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const element = event.target;

    const {
      files: fileList
    } = element;

    const files: File[] = [];

    if (fileList) {
      for (let i = 0, length = fileList.length; i < length; i++) {
        files.push(fileList[i]);
      }
    }

    this.setState({
      files: files
    });
  }

  private _onNothingHappened = (): void => {
    this.setState((previousState: IFileSystemDialogState): IFileSystemDialogState => {
      return {
        ...previousState,
        hasNothingHappened: !previousState.files
      };
    });
  }
}
